import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudio } from '../../context/AudioContext';
import { useCart } from '../../context/CartContext';
import { BettyTrack as Track } from '../../types/music';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tracks: Track[];
  totalResults: number;
  query?: string;
}

const SidebarOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent; /* Removed blocking overlay for better UX */
  z-index: 10000;
  pointer-events: none; /* Allow clicks through to background */

  @media (min-width: 1200px) {
    display: none; /* No overlay on desktop */
  }
`;

const SidebarContainer = styled(motion.div)`
  position: fixed;
  top: 80px; /* Position below navbar */
  left: 0;
  width: 380px;
  height: calc(100vh - 80px - 92px); /* Account for navbar (80px) and audio player (92px: 60px content + 32px padding) */
  background: 
    linear-gradient(135deg, 
      rgba(255, 248, 240, 0.95) 0%, 
      rgba(255, 240, 230, 0.9) 50%,
      rgba(255, 235, 220, 0.85) 100%
    );
  border-right: 1px solid rgba(255, 182, 193, 0.3);
  z-index: 10001; /* Ensure above audio player (sticky: 100) */
  overflow: hidden;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;

  @media (max-width: 1199px) {
    width: 100vw;
    max-width: 400px;
  }

  @media (max-width: 768px) {
    width: 100vw;
    height: calc(100vh - 80px - 104px); /* Extra space on mobile for larger player (60px + 44px padding) */
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(255, 182, 193, 0.4);
  position: relative;
  flex-shrink: 0; /* Don't shrink header */
  backdrop-filter: blur(10px);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;

  h3 {
    margin: 0 0 4px 0;
    color: #8b4513;
    font-size: 1.2rem;
    font-weight: bold;
  }

  p {
    margin: 0 0 8px 0;
    color: rgba(139, 69, 19, 0.8);
    font-size: 0.9rem;
    word-wrap: break-word;
  }

  .results-count {
    color: #d4af37;
    font-size: 0.85rem;
    font-weight: 500;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 182, 193, 0.3);
  color: #8b4513;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 182, 193, 0.4);
    color: #8b4513;
    border-color: rgba(255, 182, 193, 0.7);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 182, 193, 0.3);
  }
`;

const TracksContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TracksList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 182, 193, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 182, 193, 0.5);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 182, 193, 0.7);
    }
  }
`;

const TrackItem = styled.div<{ $isPlaying?: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(255, 182, 193, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  background: ${props => props.$isPlaying ? 'rgba(255, 182, 193, 0.1)' : 'transparent'};
  gap: 12px;

  &:hover {
    background: rgba(255, 182, 193, 0.15);
  }
`;

const TrackInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TrackTitle = styled.div`
  color: #8b4513;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TrackMeta = styled.div`
  color: rgba(139, 69, 19, 0.8);
  font-size: 0.8rem;
  font-weight: 400;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TrackActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
`;

const CircularButton = styled.button<{ $isPlaying?: boolean; $added?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${props => {
    if (props.$added) return '#4ade80';
    if (props.$isPlaying) return '#ff69b4';
    return 'rgba(255, 182, 193, 0.5)';
  }};
  background: ${props => {
    if (props.$added) return 'rgba(74, 222, 128, 0.2)';
    if (props.$isPlaying) return 'rgba(255, 105, 180, 0.2)';
    return 'rgba(255, 182, 193, 0.1)';
  }};
  color: ${props => {
    if (props.$added) return '#4ade80';
    if (props.$isPlaying) return '#ff69b4';
    return 'rgba(139, 69, 19, 0.8)';
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;

  &:hover {
    background: ${props => {
    if (props.$added) return 'rgba(74, 222, 128, 0.3)';
    if (props.$isPlaying) return 'rgba(255, 105, 180, 0.3)';
    return 'rgba(255, 182, 193, 0.2)';
  }};
    border-color: ${props => {
    if (props.$added) return '#4ade80';
    return '#ff69b4';
  }};
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Removed unused PlayingIndicator to reduce confusion

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: rgba(139, 69, 19, 0.6);

  h4 {
    margin: 0 0 8px 0;
    color: #8b4513;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px;
  gap: 12px;
  border-top: 1px solid rgba(255, 182, 193, 0.1);
  background: rgba(255, 255, 255, 0.9);
  flex-shrink: 0; /* Keep pagination at bottom */
  margin-top: auto; /* Push to bottom */
  backdrop-filter: blur(10px);
`;

const PaginationButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255, 182, 193, 0.5);
  background: rgba(255, 182, 193, 0.1);
  color: rgba(139, 69, 19, 0.8);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: bold;

  &:hover:not(:disabled) {
    background: rgba(255, 182, 193, 0.2);
    border-color: #ff69b4;
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

const PaginationInfo = styled.div`
  color: rgba(139, 69, 19, 0.7);
  font-size: 0.8rem;
  font-weight: 500;
  min-width: 40px;
  text-align: center;
`;

const TRACKS_PER_PAGE = 10;

export const BettySearchSidebar: React.FC<Props> = ({
  isOpen,
  onClose,
  tracks,
  totalResults,
  query
}) => {
  const { currentTrack, isPlaying, playTrack } = useAudio();
  const { addToCart } = useCart();
  const [addedTracks, setAddedTracks] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  const sidebarVariants = {
    hidden: { x: '-100%' },
    visible: { x: 0 },
    exit: { x: '-100%' }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const handlePlayTrack = (track: Track) => {
    // Convert Betty track format to audio player format
    playTrack({
      id: track.id,
      title: track.title,
      artist: track.artist,
      url: track.url,
      coverArt: track.coverArt || track.cover_art || undefined,
      album: track.album_title
    });
  };

  const handleAddToCart = (track: Track) => {
    // Convert to cart format
    const cartItem = {
      id: track.id,
      title: track.title,
      artist: track.artist,
      url: track.url,
      coverArt: undefined,
      year: track.year?.toString(),
      genre: track.genre
    };

    addToCart(cartItem);
    setAddedTracks(prev => new Set(prev).add(track.id));

    // Remove the success state after 2 seconds
    setTimeout(() => {
      setAddedTracks(prev => {
        const newSet = new Set(prev);
        newSet.delete(track.id);
        return newSet;
      });
    }, 2000);
  };

  const isCurrentTrack = (track: Track) => {
    return currentTrack?.id === track.id;
  };

  const isTrackPlaying = (track: Track) => {
    return isCurrentTrack(track) && isPlaying;
  };

  // Reset page when tracks change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [tracks]);

  // Calculate pagination
  const totalPages = Math.ceil(tracks.length / TRACKS_PER_PAGE);
  const startIndex = (currentPage - 1) * TRACKS_PER_PAGE;
  const endIndex = startIndex + TRACKS_PER_PAGE;
  const currentTracks = tracks.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <SidebarOverlay
            key="sidebar-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />
          <SidebarContainer
            key="sidebar-container"
            variants={sidebarVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <SidebarHeader>
              <HeaderContent>
                <HeaderInfo>
                  <h3>Search Results</h3>
                  {query && <p>&quot;{query}&quot;</p>}
                  <div className="results-count">
                    {totalResults} track{totalResults !== 1 ? 's' : ''} found
                    {totalPages > 1 && (
                      <span> • Page {currentPage} of {totalPages}</span>
                    )}
                  </div>
                </HeaderInfo>
                <CloseButton onClick={onClose}>
                  ×
                </CloseButton>
              </HeaderContent>
            </SidebarHeader>

            <TracksContainer>
              <TracksList>
                {tracks.length === 0 ? (
                  <EmptyState>
                    <h4>No tracks found</h4>
                    <p>Try a different search term or ask Betty to help you find something specific.</p>
                  </EmptyState>
                ) : (
                  currentTracks.map((track) => (
                    <TrackItem
                      key={track.id}
                      $isPlaying={isTrackPlaying(track)}
                    >
                      <TrackInfo>
                        <TrackTitle>{track.title}</TrackTitle>
                        <TrackMeta>
                          {track.artist}
                          {track.genre.length > 0 && ` • ${track.genre.join(', ')}`}
                          {track.year && ` • ${track.year}`}
                          {track.album_title && ` • ${track.album_title}`}
                        </TrackMeta>
                      </TrackInfo>

                      <TrackActions>
                        <CircularButton
                          $isPlaying={isTrackPlaying(track)}
                          onClick={() => handlePlayTrack(track)}
                          title={isTrackPlaying(track) ? 'Pause' : 'Play'}
                        >
                          {isTrackPlaying(track) ? (
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          )}
                        </CircularButton>
                        <CircularButton
                          $added={addedTracks.has(track.id)}
                          onClick={() => handleAddToCart(track)}
                          disabled={addedTracks.has(track.id)}
                          title={addedTracks.has(track.id) ? 'Added to cart' : 'License'}
                        >
                          {addedTracks.has(track.id) ? (
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
                            </svg>
                          )}
                        </CircularButton>
                      </TrackActions>
                    </TrackItem>
                  ))
                )}
              </TracksList>

              {/* Pagination always at bottom if there are multiple pages */}
              {totalPages > 1 && (
                <PaginationControls>
                  <PaginationButton
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </PaginationButton>
                  <PaginationInfo>
                    {currentPage} / {totalPages}
                  </PaginationInfo>
                  <PaginationButton
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </PaginationButton>
                </PaginationControls>
              )}
            </TracksContainer>
          </SidebarContainer>
        </>
      )}
    </AnimatePresence>
  );
};